import {zodResolver} from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, {useEffect} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Link as RouterLink, useLocation} from 'react-router-dom';

import {AlertInfo, PasswordInput} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import Icon from 'presentation/icons';

import {LoginEnum} from 'main/analytics/gaTagsEnum';
import {MaskedInput} from 'presentation/components/MaskedInput';
import {ForgotPasswordEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useLogin} from 'presentation/pages/public/hooks/useLogin';
import {translations} from 'presentation/translations-new';
import {masks} from 'presentation/utils';
import {IFormData, formSchema} from './schema';

const {
	actionText,
	formLabels,
	formPlaceholders,
	signIn: {portalRedirect},
} = translations['pt-br'].publicFlow;

export function SignInForm() {
	const {isGndiSaoPaulo} = useHostName();
	const location = useLocation();
	const goToSignInState = location.state?.goToSignInState || null;

	const placeholder = isGndiSaoPaulo ? formPlaceholders.signInCPF : formPlaceholders.signIn;
	const signInLabel = isGndiSaoPaulo ? formLabels.cpf : formLabels.user;

	const defaultValues = {login: '', password: ''};
	const methods = useForm<IFormData>({
		defaultValues,
		resolver: zodResolver(formSchema),
		mode: 'onChange',
		delayError: 1000,
	});

	const {
		handleSubmit,
		register,
		watch,
		getFieldState,
		reset,
		formState: {errors, isSubmitting, isValid},
	} = methods;
	const loginData = watch('login');
	const {isDirty, invalid} = getFieldState('login');
	const hasInputError = isDirty && invalid;
	const loginHelperText = isDirty ? errors?.login?.message : undefined;
	const isButtonDisabled = isSubmitting || !isValid;

	const {handleLogin, showAPIError, apiErrorMsg, portalRedirectLink} = useLogin({methods});

	const handlePortalRedirect = () => {
		window.location.href = portalRedirectLink;
	};

	useEffect(() => {
		reset(defaultValues);
	}, [goToSignInState]);

	return (
		<FormProvider {...methods}>
			<Stack component="form" gap={2} onSubmit={handleSubmit(handleLogin)}>
				<Stack>
					<Typography variant="label" component="label" htmlFor="login">
						{signInLabel}
					</Typography>
					<MaskedInput
						{...register('login')}
						id="login"
						maskOptions={masks.login}
						autoComplete="current-login"
						error={hasInputError}
						helperText={loginHelperText}
						label={placeholder}
						$fieldName="login"
					/>
				</Stack>
				<Stack>
					<Stack direction="row" justifyContent="space-between">
						<Typography variant="label" component="label" htmlFor="password">
							{formLabels.password}
						</Typography>
						<Link
							data-tag-id={LoginEnum.CLICK_ESQUECI_MINHA_SENHA}
							component={RouterLink}
							to={ForgotPasswordEnum.FORGOT_PASSWORD}
							state={{login: loginData}}>
							{actionText.forgotPassword}
						</Link>
					</Stack>
					<PasswordInput id="password" autoComplete="current-password" name="password" />
				</Stack>
				{showAPIError ? (
					<Alert severity="error" data-tag-id={LoginEnum.ERRO_LOGIN}>
						{apiErrorMsg}
					</Alert>
				) : null}
				{portalRedirectLink ? (
					<AlertInfo.Root severity="warning">
						<AlertInfo.Description>
							{portalRedirect.description}
							<AlertInfo.Action
								onClick={handlePortalRedirect}
								$iconName="arrow-right-filled"
								mt={1}>
								{portalRedirect.actionText}
							</AlertInfo.Action>
						</AlertInfo.Description>
					</AlertInfo.Root>
				) : null}

				<Button
					size="large"
					disabled={isButtonDisabled}
					type="submit"
					data-tag-id={LoginEnum.CLICK_ACESSAR_CONTA}
					endIcon={
						isSubmitting ? (
							<Loading color="inherit" size={16} />
						) : (
							<Icon name="log-in" />
						)
					}>
					{actionText.signIn}
				</Button>
			</Stack>
		</FormProvider>
	);
}
